import { useState, useEffect, useRef } from 'react';

export default function WhatYouGet() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const galleryRef = useRef<HTMLDivElement>(null);
  const [galleryVisible, setGalleryVisible] = useState(false);

  const deliverables = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Window count, estimated sizing, and layout',
      description: '(based on exterior photos)'
    },
    {
      icon: 'ri-image-line',
      title: 'Recommended window option + visuals',
      description: 'See exactly what you\'re getting'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Energy-efficiency details',
      description: 'and a simple project timeline'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'A next-step button',
      description: 'to approve + schedule final measurements'
    }
  ];

  const galleryPhotos = [
    {
      id: 1,
      image: 'https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/ae9c309d6aa7d749debb82a0c34db932.jpeg',
      caption: 'Professional Installation'
    },
    {
      id: 2,
      image: 'https://public.readdy.ai/ai/img_res/3e0fc7b42e5aeba53ded755121090da5.jpg',
      caption: 'Completed Project'
    },
    {
      id: 3,
      image: 'https://readdy.ai/api/search-image?query=Close%20up%20detail%20shot%20of%20premium%20window%20frame%20installation%20showing%20precise%20weatherproofing%20and%20insulation%20work%20professional%20quality%20finish%20on%20residential%20home&width=600&height=400&seq=gallery3&orientation=landscape',
      caption: 'Quality Details'
    },
    {
      id: 4,
      image: 'https://readdy.ai/api/search-image?query=Before%20and%20after%20window%20transformation%20showing%20old%20worn%20window%20replaced%20with%20modern%20energy%20efficient%20double%20pane%20window%20dramatic%20improvement%20on%20home%20exterior&width=600&height=400&seq=gallery4&orientation=landscape',
      caption: 'Transformation'
    },
    {
      id: 5,
      image: 'https://readdy.ai/api/search-image?query=Team%20of%20professional%20window%20installers%20working%20together%20on%20large%20residential%20project%20showing%20coordination%20and%20expertise%20safety%20equipment%20visible%20clean%20worksite&width=600&height=400&seq=gallery5&orientation=landscape',
      caption: 'Expert Team'
    },
    {
      id: 6,
      image: 'https://readdy.ai/api/search-image?query=Stunning%20bay%20window%20installation%20completed%20on%20beautiful%20home%20showing%20architectural%20detail%20and%20premium%20craftsmanship%20natural%20light%20flooding%20interior%20visible%20through%20glass&width=600&height=400&seq=gallery6&orientation=landscape',
      caption: 'Premium Results'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setGalleryVisible(true);
            galleryPhotos.forEach((_, index) => {
              setTimeout(() => {
                setVisibleItems((prev) => [...prev, index]);
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (galleryRef.current) {
      observer.observe(galleryRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="what-you-get" className="py-24 bg-white">
      <div className="max-w-[1000px] mx-auto px-6">
        {/* Headline */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-[#2B6777] mb-4 tracking-tight">
            What Your Proposal Includes
          </h2>
        </div>

        {/* Deliverables Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {deliverables.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-6 bg-gradient-to-br from-[#C8D8E4]/10 to-white rounded-2xl border border-[#C8D8E4]/30"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-[#52AB98] rounded-xl flex-shrink-0">
                <i className={`${item.icon} text-white text-xl`}></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#2B6777] mb-1">
                  {item.title}
                </h3>
                <p className="text-base text-[#2B6777]/60">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Premium Photo Gallery */}
        <div ref={galleryRef} className="mt-20">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#52AB98]/10 text-[#52AB98] text-sm font-medium rounded-full mb-4">
              Our Work in Action
            </span>
            <h3 className="text-3xl md:text-4xl font-heading font-semibold text-[#2B6777] tracking-tight">
              See the Proof
            </h3>
            <p className="text-[#2B6777]/60 mt-3 max-w-lg mx-auto">
              Real projects, real results. Every window tells a story of quality craftsmanship.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {galleryPhotos.map((photo, index) => (
              <div
                key={photo.id}
                className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-700 ease-out ${
                  visibleItems.includes(index)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="aspect-[3/2] w-full overflow-hidden">
                  <img
                    src={photo.image}
                    alt={photo.caption}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2B6777]/90 via-[#2B6777]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-white font-semibold text-lg">{photo.caption}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-8 h-0.5 bg-[#52AB98]"></div>
                      <span className="text-[#52AB98] text-sm font-medium">View Details</span>
                    </div>
                  </div>
                </div>

                {/* Corner accent */}
                <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:rotate-0 rotate-45">
                  <i className="ri-zoom-in-line text-[#2B6777] text-lg"></i>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom accent line */}
          <div className={`flex items-center justify-center gap-3 mt-12 transition-all duration-1000 delay-700 ${galleryVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#52AB98]/50"></div>
            <div className="w-2 h-2 rounded-full bg-[#52AB98]"></div>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#52AB98]/50"></div>
          </div>
        </div>
      </div>
    </section>
  );
}